import React, { useState, useEffect, useMemo } from 'react';
import StarRating from './StarRating.js';

import Chart from "react-apexcharts";

import loadingimg from '../img/loading.svg';

import { formatNumber, getBadgeCompletion } from './functions.js';

import FeedbackCard from './FeedbackCard.js';

const OPENWEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

const WEATHER_LABELS = {
  Clear: 'Sonnig',
  Clouds: 'Bewölkt',
  Rain: 'Regen',
  Drizzle: 'Nieselregen',
  Thunderstorm: 'Gewitter',
  Snow: 'Schnee',
  Mist: 'Nebel',
  Smoke: 'Rauch',
  Haze: 'Dunst',
  Dust: 'Staub',
  Fog: 'Nebel',
  Sand: 'Sand',
  Ash: 'Asche',
  Squall: 'Böen',
  Tornado: 'Tornado',
};

function normalizeChartPoints(chartData = []) {
  return chartData
    .map((point) => {
      if (Array.isArray(point)) {
        return {
          timestamp: Number(point[0]),
          stationCount: Number(point[1]) || 0,
          userCount: Number(point[2]) || 0,
        };
      }

      if (point && typeof point === 'object') {
        return {
          timestamp: Number(point.x),
          stationCount: Number(point.y ?? point.stationCount) || 0,
          userCount: Number(point.userCount ?? point.users ?? point.z) || 0,
        };
      }

      return null;
    })
    .filter((point) => point && Number.isFinite(point.timestamp) && Number.isFinite(point.stationCount) && Number.isFinite(point.userCount));
}

function getDateKeyFromTimestamp(timestamp) {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function shiftDateKey(dateKey, days) {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function getMiddayTimestamp(dateKey) {
  return new Date(`${dateKey}T12:00:00Z`).getTime();
}

function formatDayLabel(dateKey) {
  return new Date(`${dateKey}T00:00:00Z`).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

const TrailModal = ({trail, onClose, ...props }) => {

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [weatherByDate, setWeatherByDate] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherStatus, setWeatherStatus] = useState('idle');

  // Ensure hooks are called unconditionally
  useEffect(() => {
    if (!trail) return;


    setLoading(true);
    setData({});

    // Define an async function to fetch data
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}traildetail.php?trailid=${trail.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`, // Add the Authorization header
            'Content-Type': 'application/json'  // Ensure the content type is set
          }
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();
        console.log("new data: ");
        console.log(result);
        setData(result);
        setLoading(false);
      } catch (error) {
        console.error(error);
      }
    };

    // Call the async function
    fetchData();
  }, [trail]);


  const maxOpened = useMemo(() => {
    return data?.stations?.reduce((max, item) => {
      const openedValue = parseInt(item.opened, 10);
      return openedValue > max ? openedValue : max;
    }, 0);
  }, [data]);

  const maxRating = useMemo(() => {
    return data?.ratingOverview?.reduce((max, item) => {
      const openedValue = parseInt(item, 10);
      return openedValue > max ? openedValue : max;
    }, 0);
  }, [data]);

  const latestFiveStarTrailFeedback = useMemo(() => {
    return (data?.feedback || [])
      .filter((item) => Number(item.rating) === 5 && item.feedback && item.feedback.trim())
      .filter((item) => String(item.id) === String(trail?.id))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 3);
  }, [data?.feedback, trail?.id]);

  const stationCountData = useMemo(() => ({
    options: {
      dataLabels: {
        enabled: false
      },
      chart: {
        id: 'station-count-bar-chart'
      },
      xaxis: {
        categories: data?.stationcount?.map(item => item.station_count),
        title: {
          text: 'Wie viele Stationen wurden angesehen'
        }
      },
      yaxis: {
        title: {
          text: 'Anzahl an Besuchern'
        }
      },
    },
    series: [
      {
        name: 'Besucher',
        data: data?.stationcount?.map(item => parseInt(item.user_count, 10))
      }
    ]
  }), [data]);

  const trailUsersOverTimeChart = useMemo(() => {
    const chartData = data?.trail_station_views_chart || data?.trail_users_chart || data?.newusers_chart || [];
    const normalizedChartData = normalizeChartPoints(chartData);

    return {
      hasData: normalizedChartData.length > 0,
      options: {
        chart: {
          id: 'trail-users-overtime',
          type: 'line',
          zoom: {
            enabled: true,
          },
        },
        xaxis: {
          type: 'datetime',
        },
        tooltip: {
          shared: true,
          x: {
            formatter: (value) => new Date(value).toLocaleDateString('de-DE', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }),
          },
        },
        title: {
          text: 'Nutzung auf diesem Weg',
          align: 'left',
        },
        stroke: {
          curve: 'smooth',
        },
        markers: {
          size: 0,
        },
        legend: {
          position: 'top',
          fontSize: '16px',
        },
      },
      series: [
        {
          name: 'Angesehene Stationen',
          data: normalizedChartData.map((point) => [point.timestamp, point.stationCount]),
        },
        {
          name: 'Familien / Nutzer',
          data: normalizedChartData.map((point) => [point.timestamp, point.userCount]),
        },
      ],
    };
  }, [data]);

  const recentTrailActivity = useMemo(() => {
    const chartData = data?.trail_station_views_chart || data?.trail_users_chart || data?.newusers_chart || [];
    const normalizedChartData = normalizeChartPoints(chartData);

    if (!normalizedChartData.length) {
      return [];
    }

    const usageByDate = normalizedChartData.reduce((acc, point) => {
      const dateKey = getDateKeyFromTimestamp(point.timestamp);
      acc[dateKey] = (acc[dateKey] || 0) + point.stationCount;
      return acc;
    }, {});

    const sortedDateKeys = Object.keys(usageByDate).sort();
    const lastDateKey = sortedDateKeys[sortedDateKeys.length - 1];

    return Array.from({ length: 14 }, (_, index) => {
      const dateKey = shiftDateKey(lastDateKey, index - 13);

      return {
        dateKey,
        label: formatDayLabel(dateKey),
        usage: usageByDate[dateKey] || 0,
        weatherTimestamp: getMiddayTimestamp(dateKey),
      };
    }).reverse();
  }, [data]);

  const maxRecentTrailUsage = useMemo(() => {
    return recentTrailActivity.reduce((max, day) => {
      return day.usage > max ? day.usage : max;
    }, 0);
  }, [recentTrailActivity]);

  useEffect(() => {
    const lat = data?.position?.lat;
    const lon = data?.position?.long ?? data?.position?.lon;

    if (!recentTrailActivity.length || lat == null || lon == null) {
      setWeatherByDate({});
      setWeatherLoading(false);
      setWeatherStatus('idle');
      return;
    }

    if (!OPENWEATHER_API_KEY) {
      setWeatherByDate({});
      setWeatherLoading(false);
      setWeatherStatus('missing-key');
      console.warn('Weather data is disabled because REACT_APP_OPENWEATHER_API_KEY is not set for this build.');
      return;
    }

    let cancelled = false;

    const fetchWeather = async () => {
      setWeatherLoading(true);
      setWeatherStatus('loading');

      const entries = await Promise.all(recentTrailActivity.map(async (day) => {
        try {
          const response = await fetch(`https://api.openweathermap.org/data/3.0/onecall/timemachine?lat=${lat}&lon=${lon}&dt=${Math.floor(day.weatherTimestamp / 1000)}&appid=${OPENWEATHER_API_KEY}`);

          if (!response.ok) {
            throw new Error(`Weather request failed with status ${response.status}`);
          }

          const result = await response.json();
          const weather = result?.data?.[0]?.weather?.[0];
          const weatherMain = weather?.main;

          return [day.dateKey, {
            label: WEATHER_LABELS[weatherMain] || weatherMain || '–',
            icon: weather?.icon || null,
          }];
        } catch (error) {
          console.error(error);
          return [day.dateKey, {
            label: '–',
            icon: null,
          }];
        }
      }));

      if (!cancelled) {
        setWeatherByDate(Object.fromEntries(entries));
        setWeatherLoading(false);
        const hasWeatherData = entries.some(([, value]) => value.icon || value.label !== '–');
        setWeatherStatus(hasWeatherData ? 'ready' : 'error');
      }
    };

    fetchWeather();

    return () => {
      cancelled = true;
    };
  }, [data?.position?.lat, data?.position?.long, data?.position?.lon, recentTrailActivity]);

  if (!trail) {
    return null; // Render nothing if trail is null
  }

  const badgeCompletion = getBadgeCompletion(trail.done, trail.started);
  const newstarted = badgeCompletion.startedOrDone;

  return (
    <div className={`modal-overlay ${trail == null ? "modalclosed" : ""}`}>
      <div onClick={onClose} className='modalbackground'></div>
      <div className="modal-content">
        <button onClick={onClose} className="close-button">Schließen X</button>
        <h2 className='trailheadline'>{trail.title}</h2>
        <div className='trailboxdatagrid'>
          <div className='trailboxdatabox'>
            <p className='trailboxdata-val'>{formatNumber(newstarted)}</p>
            <p className='trailboxdata-label'>Gestartet</p>
          </div>

          <div className='trailboxdatabox'>
            <p className='trailboxdata-val'>{formatNumber(trail.done)}</p>
            <p className='trailboxdata-label'>Badges</p>
          </div>

          <div className='trailboxdatabox'>
            <p className='trailboxdata-val'>
              {badgeCompletion.percentage == null
                ? <span className='trailboxdata-empty'>–</span>
                : <>{badgeCompletion.percentage}<span className='val-smalltext'>%</span></>
              }
            </p>
            <p className='trailboxdata-label'>Badge-Abschluss</p>
            {badgeCompletion.benchmarkBadge && (
              <span className={`kpi-context-badge kpi-context-badge-${badgeCompletion.benchmarkBadge.tone}`}>
                {badgeCompletion.benchmarkBadge.label}
              </span>
            )}
          </div>

          <div className='trailboxdatabox'>
            <p className='trailboxdata-val'>{trail['rating-avg'] ? parseFloat(trail['rating-avg']).toFixed(1) : <span style={{ opacity: 0.4 }}>–</span>}</p>
            <p className='trailboxdata-label'>Bewertung ({trail['rating-count']})</p>
            {trail['rating-avg'] && <StarRating rating={parseFloat(trail['rating-avg'])} width={60} />}
          </div>
        </div>
        <p className='tiptext kpi-context-text'>Viele Familien erkunden Wege flexibel, auch in Teilstrecken. Der Badge-Abschluss zeigt, wie oft ein Start bis zum Badge geführt hat.</p>

        {
          latestFiveStarTrailFeedback.length > 0 && (
            <div
              className='feedbackgrid3 trailfeedbackgrid'
              style={{ gridTemplateColumns: `repeat(${latestFiveStarTrailFeedback.length}, minmax(0, 1fr))` }}
            >
              {
                latestFiveStarTrailFeedback.map((obj, index) => (
                  <FeedbackCard key={index} {...obj} slug={data.region.slug} compact />
                ))
              }
            </div>
          )
        }

        {
          (loading || trailUsersOverTimeChart.hasData) && (
            <div className='container trailchartcontainer'>
              {
                loading
                  ?
                  <div className='loadingcont trailchartloading'><img src={loadingimg} alt="" /></div>
                  :
                  <Chart
                    id="trailusersovertime"
                    options={trailUsersOverTimeChart.options}
                    series={trailUsersOverTimeChart.series}
                    type="line"
                    height="320"
                  />
              }
            </div>
          )
        }

        {
          !loading && recentTrailActivity.length > 0 && (
            <div className='container trailweathercontainer'>
              <h2 className='trailchartheadline'>Wetter letzte 14 Tage</h2>
              <p className='tiptext'>Hier sieht man pro Tag die Nutzung (angesehene Stationen) und das Wetter am Standort des Weges.</p>
              {
                weatherStatus === 'missing-key' && (
                  <p className='tiptext'>Wetterdaten sind in dieser Umgebung nicht konfiguriert.</p>
                )
              }
              {
                weatherStatus === 'error' && (
                  <p className='tiptext'>Wetterdaten konnten gerade nicht geladen werden.</p>
                )
              }
              <div className='trailweathergrid'>
                <div className='trailweatherrow trailweatherrowhead'>
                  <p>Tag</p>
                  <p>Nutzung</p>
                  <p>Wetter</p>
                </div>
                {
                  recentTrailActivity.map((day) => (
                    <div className='trailweatherrow' key={day.dateKey}>
                      <p>{day.label}</p>
                      <div className='trailusagecell'>
                        <div className='trailusagebar'>
                          <div
                            className='trailusagebarinner'
                            style={{ width: `${maxRecentTrailUsage === 0 ? 0 : (day.usage / maxRecentTrailUsage) * 100}%` }}
                          ></div>
                        </div>
                        <span className='trailusagevalue'>{formatNumber(day.usage)}</span>
                      </div>
                      <p className='trailweathercell'>
                        {
                          weatherLoading
                            ? '…'
                            : (
                              <>
                                {
                                  weatherByDate[day.dateKey]?.icon && (
                                    <img
                                      src={`https://openweathermap.org/img/wn/${weatherByDate[day.dateKey].icon}.png`}
                                      alt={weatherByDate[day.dateKey].label}
                                      className='trailweathericon'
                                    />
                                  )
                                }
                                <span>{weatherByDate[day.dateKey]?.label || '–'}</span>
                              </>
                            )
                        }
                      </p>
                    </div>
                  ))
                }
              </div>
            </div>
          )
        }

        {
          loading
            ?
            <div className='loadingcont'><img src={loadingimg} alt="" /></div>
            :
            <div className='traildetaildatacont'>
              <h2 style={{ marginTop: 56 }}>{data?.stations?.length} Stationen</h2>
              <p className='tiptext'>Hier sieht man, wie oft die Stationen jeweils angesehen wurden. Die erste Station wird typischerweise am häufigsten geöffnet, danach verteilt sich die Nutzung natürlicherweise über Teilstrecken.</p>
              {
                data?.stations?.map((obj, index) =>
                  <div className='stationbox' key={obj.id || index}>
                    <div className='stationboxtext'>
                      <div className='stationboxtitle'>
                        <div className='stationboximage' style={{ backgroundImage: `url("https://static.wunderweg.com/clients/${data.region.slug}/${trail.id}/${obj.id}/image.png")` }}></div>
                        <h3>{obj.title}</h3>
                      </div>
                      {obj?.opened}
                    </div>
                    <div className='stationbox-bar'>
                      <div className='stationbox-bar-inner' style={{ width: obj.opened / maxOpened * 100 + "%" }}></div>
                    </div>
                  </div>
                )
              }

              <h2 style={{ marginTop: 56 }}>Nutzungstiefe</h2>
              <p className='tiptext'>Viele Familien erkunden Wege in ihrem eigenen Tempo und manchmal nur in Teilstrecken. Die Ansicht zeigt, wie viele der {data?.stations?.length} Stationen bis zum letzten angesehenen Punkt geöffnet wurden.</p>
              <Chart options={stationCountData.options} series={stationCountData.series} type="bar" width="100%" height={500} />

              <h2 className='sectionheadline'>Feedback</h2>
              <div className='ratingdetailbox'>
                <div>
                  <p className='trailboxdata-val'>{trail['rating-avg'] ? parseFloat(trail['rating-avg']).toFixed(1) : <span style={{ opacity: 0.4 }}>–</span>}</p>
                  <p className='trailboxdata-label'>Bewertung ({trail['rating-count']})</p>
                  {trail['rating-avg'] && <StarRating rating={parseFloat(trail['rating-avg'])} width={100} />}
                </div>
                <div>
                  {
                    data?.ratingOverview?.map((obj, index) =>
                      <div className='ratingdetailbarcontainer' key={5 - index}>
                        <p>{5 - index}</p>
                        <div className='ratingdetail-bar'>
                          <div className='ratingdetail-bar-inner' style={{ width: obj / maxRating * 100 + "%" }}></div>
                        </div>
                      </div>
                    )
                  }
                </div>
              </div>
              <div className='feedbackcontainer'>
                {
                  data?.feedback?.map((obj, index) => (
                    <FeedbackCard key={index} {...obj} slug={data.region.slug} />
                  ))
                }
              </div>
              <div className='secondarybtn' onClick={() => props.closeallandopenFeedback()}>Alle anzeigen</div>
            </div>
        }




      </div>
    </div>

  );
};

export default TrailModal;
